import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { ConfirmActionButton } from '~/components/overlays';
import { Button } from '~/components/ui';
import { RetryBanner } from '~/components/ui/RetryBanner';
import { SectionPanel } from '~/components/ui/SectionPanel';
import {
  useDeleteSellerList,
  useMySellerLists,
  type SellerList,
} from '~/api/sellerLists';
import { sellerLabel, toastGraphQLError } from '~/utils';
import { SellerListModal } from './SellerListModal';

export function SellerListsSection() {
  const { t } = useTranslation('organizations');
  const { data, isLoading, error, refetch } = useMySellerLists();
  const deleteSellerList = useDeleteSellerList();
  const [editing, setEditing] = useState<SellerList | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sellerLists = data?.mySellerLists ?? [];

  const [isOpening, setIsOpening] = useState(false);
  const latestRequest = useRef(0);

  useEffect(
    () => () => {
      latestRequest.current++;
    },
    []
  );

  const openModal = async (sellerList: SellerList | null) => {
    const request = ++latestRequest.current;
    if (!sellerList) {
      setEditing(null);
      setIsModalOpen(true);
      return;
    }
    setIsOpening(true);
    try {
      const { data: fresh } = await refetch({ throwOnError: true });
      if (request !== latestRequest.current) return;
      const row = fresh?.mySellerLists.find(({ id }) => id === sellerList.id);
      if (!row) {
        toast.error(t('organizations.sellerLists.notFound'));
        return;
      }
      setEditing(row);
      setIsModalOpen(true);
    } catch (error) {
      if (request === latestRequest.current) toastGraphQLError(error);
    } finally {
      if (request === latestRequest.current) setIsOpening(false);
    }
  };

  const closeModal = () => {
    latestRequest.current++;
    setIsOpening(false);
    setIsModalOpen(false);
  };

  return (
    <section data-testid="seller-lists">
      <SectionPanel
        title={t('organizations.sellerLists.title')}
        aside={
          <Button
            size="sm"
            width="auto"
            disabled={isOpening}
            onClick={() => void openModal(null)}
            data-testid="seller-list-create"
          >
            <Plus size={16} className="mr-1" />
            {t('organizations.sellerLists.create')}
          </Button>
        }
      >
        {isLoading ? (
          <p className="text-sm text-neut-sec">
            {t('organizations.sellerLists.loading')}
          </p>
        ) : error && !data ? (
          <RetryBanner
            message={t('organizations.sellerLists.loadFailed')}
            retryLabel={t('organizations.sellerLists.retry')}
            onRetry={() => void refetch()}
            testId="seller-lists"
          />
        ) : sellerLists.length === 0 ? (
          <p className="text-sm text-neut-sec">
            {t('organizations.sellerLists.empty')}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-neut-border">
            {sellerLists.map((sellerList) => (
              <li
                key={sellerList.id}
                data-testid={`seller-list-${sellerList.id}`}
                className="flex items-center justify-between gap-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-neut-prim">
                    {sellerList.name}
                  </p>
                  <p className="text-xs text-neut-sec truncate">
                    {t('organizations.sellerLists.sellerCount', {
                      count: sellerList.members.length,
                    })}
                    {sellerList.members.length > 0 &&
                      ` · ${sellerList.members.map(({ seller }) => sellerLabel(seller)).join(', ')}`}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    width="auto"
                    variant="secondary"
                    disabled={isOpening}
                    onClick={() => void openModal(sellerList)}
                  >
                    {t('organizations.sellerLists.edit')}
                  </Button>
                  <ConfirmActionButton
                    triggerLabel={t('organizations.sellerLists.delete')}
                    triggerProps={{
                      variant: 'destructive-outline',
                      disabled: isOpening,
                    }}
                    title={t('organizations.sellerLists.deleteTitle')}
                    message={t('organizations.sellerLists.deleteMessage', {
                      name: sellerList.name,
                    })}
                    cancelLabel={t('organizations.sellerLists.cancel')}
                    confirmLabel={t('organizations.sellerLists.delete')}
                    successMessage={t('organizations.sellerLists.deleted')}
                    isPending={deleteSellerList.isPending}
                    onConfirm={() =>
                      deleteSellerList.mutateAsync({ id: sellerList.id })
                    }
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionPanel>
      {isModalOpen && (
        <SellerListModal
          key={editing ? editing.id : 'new'}
          sellerList={editing}
          onClose={closeModal}
        />
      )}
    </section>
  );
}
